import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/potrdi-email")({
  head: () => ({
    meta: [
      { title: "Potrditev e-pošte — kupish.si" },
      { name: "robots", content: "noindex,nofollow" },
    ],
      links: [{ rel: "canonical", href: "https://kupish.si/potrdi-email" }],
  }),
  component: Page,
});

type Status = "checking" | "confirmed" | "pending" | "error";

function Page() {
  const [status, setStatus] = useState<Status>("checking");
  const [email, setEmail] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // 1) Preveri morebitno napako iz hash-a (npr. potekel/neveljaven token)
    if (typeof window !== "undefined") {
      const hash = window.location.hash || "";
      if (hash.includes("error")) {
        const params = new URLSearchParams(hash.replace(/^#/, ""));
        const desc = params.get("error_description") ?? params.get("error");
        if (desc) {
          setErrorMsg(decodeURIComponent(desc.replace(/\+/g, " ")));
          setStatus("error");
          return;
        }
      }
    }

    // 2) Naroči se na auth event: po kliku na link iz e-pošte Supabase vzpostavi sejo.
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if ((event === "SIGNED_IN" || event === "USER_UPDATED") && session?.user) {
        setEmail(session.user.email ?? null);
        setStatus(session.user.email_confirmed_at ? "confirmed" : "pending");
      }
    });

    // 3) Fallback: če dogodka ni (osvežena stran), preveri trenutnega uporabnika.
    (async () => {
      // Kratek zamik, da Supabase obdela hash
      await new Promise((r) => setTimeout(r, 250));
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (data.user) {
        setEmail(data.user.email ?? null);
        setStatus(data.user.email_confirmed_at ? "confirmed" : "pending");
      } else {
        // Brez seje in brez napake v hash-u — uporabnik je prišel brez konteksta.
        setStatus("pending");
      }
    })();

    return () => {
      cancelled = true;
      sub.data.subscription.unsubscribe();
    };
  }, []);

  async function onResend() {
    if (!email) return;
    setResendError(null);
    setResent(false);
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/potrdi-email` },
    });
    setResending(false);
    if (error) setResendError(error.message);
    else setResent(true);
  }

  return (
    <section className="mx-auto max-w-md px-6 py-24">
      <div className="eyebrow">Potrditev</div>
      <h1 className="font-serif text-5xl mt-3">Potrditev e-pošte</h1>

      {status === "checking" && (
        <p className="mt-6 text-sm text-muted-foreground">Preverjam stanje…</p>
      )}

      {status === "confirmed" && (
        <div className="mt-10 border border-border p-6 text-sm space-y-4">
          <p>
            <strong>Hvala!</strong> E-naslov{" "}
            {email && <span className="text-foreground">{email}</span>} je
            potrjen. Vaš račun je aktiven.
          </p>
          <Link
            to="/dashboard"
            className="inline-block bg-primary text-primary-foreground px-6 py-3 text-sm hover:opacity-90"
          >
            V moj račun
          </Link>
        </div>
      )}

      {status === "pending" && (
        <div className="mt-10 border border-border p-6 text-sm space-y-4">
          <p>
            E-naslov <strong>{email ?? "—"}</strong> še <strong>ni potrjen</strong>. Odprite
            povezavo v sporočilu, ki smo vam ga poslali ob registraciji.
          </p>
          <p className="text-muted-foreground">
            Če e-pošte ne najdete, preverite mapo z neželeno pošto.
          </p>
          {resent ? (
            <p className="text-sm">
              Nova povezava za potrditev je bila poslana na <strong>{email}</strong>.
            </p>
          ) : (
            <button
              type="button"
              onClick={onResend}
              disabled={!email || resending}
              className="bg-primary text-primary-foreground px-6 py-3 text-sm hover:opacity-90 disabled:opacity-60"
            >
              {resending ? "Pošiljam…" : "Pošlji novo povezavo"}
            </button>
          )}
          {resendError && (
            <p className="text-sm text-destructive">{resendError}</p>
          )}
          <p className="text-sm">
            <Link to="/prijava" className="underline underline-offset-4 hover:text-accent">
              Nazaj na prijavo
            </Link>
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="mt-10 border border-border p-6 text-sm space-y-4">
          <p>
            Potrditev ni uspela:{" "}
            {errorMsg ?? "povezava je neveljavna ali je potekla."}
          </p>
          <p>
            <Link to="/registracija" className="underline underline-offset-4 hover:text-accent">
              Ustvarite nov račun
            </Link>{" "}
            ali se{" "}
            <Link to="/prijava" className="underline underline-offset-4 hover:text-accent">
              prijavite
            </Link>
            , če že imate potrjen račun.
          </p>
        </div>
      )}
    </section>
  );
}
